import { Link, useNavigate } from 'react-router-dom';
import { 
  X, 
  ArrowRight, 
  Scale, 
  Gauge, 
  Fuel, 
  Cog, 
  Weight, 
  Zap, 
  Phone, 
  Bitcoin,
  Plus,
  CheckCircle,
  XCircle,
  Trophy,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCompare } from '@/context/CompareContext';

export default function ComparePage() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();
  const navigate = useNavigate();

  if (compareList.length === 0) {
    return (
      <main className="min-h-screen bg-slate-50" data-testid="compare-page-empty">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
          <div className="container-custom text-center">
            <div className="w-24 h-24 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <Scale className="w-12 h-12 text-slate-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">No Engines to Compare</h1>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              Add engines to your comparison list from the inventory to see them side by side and make informed decisions.
            </p>
            <Link to="/inventory">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
                Browse Inventory
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* How to compare guide */}
        <div className="container-custom py-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-12">How to Compare Engines</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Browse Inventory</h3>
              <p className="text-slate-600 text-sm">Explore our selection of marine engines and find ones that interest you.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Add to Compare</h3>
              <p className="text-slate-600 text-sm">Click the compare button on any engine card to add it to your list (up to 4).</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Compare & Decide</h3>
              <p className="text-slate-600 text-sm">View all specifications side by side and choose the perfect engine.</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Specs to compare
  const specs = [
    { key: 'brand', label: 'Brand', icon: null },
    { key: 'model', label: 'Model', icon: null },
    { key: 'condition', label: 'Condition', icon: null },
    { key: 'horsepower', label: 'Horsepower', suffix: ' HP', icon: Gauge, highlight: true },
    { key: 'engine_type', label: 'Engine Type', icon: null },
    { key: 'fuel_type', label: 'Fuel Type', icon: Fuel },
    { key: 'cylinders', label: 'Cylinders', icon: Cog },
    { key: 'displacement', label: 'Displacement', suffix: ' L', icon: null },
    { key: 'weight', label: 'Weight', suffix: ' kg', icon: Weight },
    { key: 'rpm_range', label: 'RPM Range', icon: Zap },
    { key: 'hours', label: 'Hours', icon: null },
    { key: 'tier_rating', label: 'Tier Rating', icon: null },
    { key: 'price', label: 'Price', prefix: '$', format: (v) => v?.toLocaleString(), icon: null, highlight: true },
  ];

  // Find best values for highlighting
  const findBest = (key, higherIsBetter = true) => {
    const values = compareList.map(e => e[key]).filter(v => v != null);
    if (values.length === 0) return null;
    return higherIsBetter ? Math.max(...values) : Math.min(...values);
  };

  const bestHP = findBest('horsepower', true);
  const bestPrice = findBest('price', false);

  // Empty slots
  const emptySlots = Math.max(0, 4 - compareList.length);

  return (
    <main className="min-h-screen bg-slate-50" data-testid="compare-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container-custom py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <Scale className="w-8 h-8 text-orange-500" />
                Compare Engines
              </h1>
              <p className="text-slate-400">
                Comparing {compareList.length} engine{compareList.length > 1 ? 's' : ''} • Up to 4 engines allowed
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/inventory">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add More
                </Button>
              </Link>
              <Button variant="ghost" onClick={clearCompare} className="text-red-400 hover:text-red-300 hover:bg-red-900/20">
                Clear All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="container-custom py-8">
        {/* Engine Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {compareList.map((engine) => (
            <div key={engine.id} className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden group">
              <div className="relative">
                <button
                  onClick={() => removeFromCompare(engine.id)}
                  className="absolute top-2 right-2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-red-100 hover:text-red-500 transition-colors shadow-md"
                  data-testid={`remove-compare-${engine.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
                <Link to={`/engine/${engine.id}`}>
                  <div className="aspect-[4/3] bg-slate-100">
                    <img
                      src={engine.images?.[0] || 'https://images.pexels.com/photos/163236/luxury-yacht-boat-speed-water-163236.jpeg'}
                      alt={engine.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                {engine.horsepower === bestHP && (
                  <Badge className="absolute top-2 left-2 bg-green-500">
                    <Trophy className="w-3 h-3 mr-1" />
                    Most Powerful
                  </Badge>
                )}
                {engine.price === bestPrice && engine.price !== bestHP && (
                  <Badge className="absolute top-2 left-2 bg-blue-500">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Best Value
                  </Badge>
                )}
              </div>
              <div className="p-4">
                <Link to={`/engine/${engine.id}`}>
                  <h3 className="font-semibold text-slate-900 hover:text-orange-500 transition-colors line-clamp-2 text-sm mb-2">
                    {engine.title}
                  </h3>
                </Link>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-orange-600">
                    ${engine.price?.toLocaleString() || 'Call'}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {engine.condition || 'Used'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs"
                    onClick={() => navigate(`/checkout/${engine.id}`)}
                  >
                    <Bitcoin className="w-3 h-3 mr-1" />
                    Buy
                  </Button>
                  <Link to={`/contact?engine=${engine.id}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full text-xs">
                      <Phone className="w-3 h-3 mr-1" />
                      Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
          
          {/* Empty slots */}
          {Array.from({ length: emptySlots }).map((_, idx) => (
            <Link 
              key={`empty-${idx}`} 
              to="/inventory"
              className="bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center min-h-[300px] hover:border-orange-500 hover:bg-orange-50 transition-colors group"
            >
              <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mb-4 group-hover:bg-orange-100">
                <Plus className="w-8 h-8 text-slate-400 group-hover:text-orange-500" />
              </div>
              <p className="text-slate-500 font-medium group-hover:text-orange-600">Add Engine</p>
              <p className="text-xs text-slate-400">Slot {compareList.length + idx + 1} of 4</p>
            </Link>
          ))}
        </div>

        {/* Specs Table */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold text-slate-900">Detailed Specifications</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {specs.map((spec, idx) => {
                  const Icon = spec.icon;
                  return (
                    <tr key={spec.key} className={`${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'} ${spec.highlight ? 'font-semibold' : ''}`}>
                      <td className="px-6 py-4 text-slate-900 w-48">
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className="w-4 h-4 text-orange-500" />}
                          {spec.label}
                        </div>
                      </td>
                      {compareList.map((engine) => {
                        let value = engine[spec.key];
                        if (spec.format && value) value = spec.format(value);
                        if (value && spec.prefix) value = spec.prefix + value;
                        if (value && spec.suffix) value = value + spec.suffix;
                        
                        const isBest = (spec.key === 'horsepower' && engine.horsepower === bestHP) ||
                                       (spec.key === 'price' && engine.price === bestPrice);
                        
                        return (
                          <td key={engine.id} className={`px-6 py-4 font-mono text-sm ${isBest ? 'text-green-600 font-bold' : 'text-slate-600'}`}>
                            <div className="flex items-center gap-2">
                              {value || 'N/A'}
                              {isBest && <CheckCircle className="w-4 h-4 text-green-500" />}
                            </div>
                          </td>
                        );
                      })}
                      {Array.from({ length: emptySlots }).map((_, idx) => (
                        <td key={`empty-cell-${idx}`} className="px-6 py-4 text-slate-300">—</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Features Comparison */}
        {compareList.some(e => e.features?.length > 0) && (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden mt-8">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <h2 className="font-bold text-slate-900">Features Comparison</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {compareList.map((engine) => (
                  <div key={engine.id}>
                    <h4 className="font-semibold text-slate-900 mb-3 text-sm">{engine.brand} {engine.model}</h4>
                    {engine.features?.length > 0 ? (
                      <ul className="space-y-2">
                        {engine.features.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400">No features listed</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Need Help Deciding?</h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Our marine engine experts can help you choose the perfect engine for your vessel. Get personalized recommendations based on your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+12012344851">
              <Button className="bg-white text-orange-600 hover:bg-slate-100">
                <Phone className="w-4 h-4 mr-2" />
                Call an Expert
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Request Consultation
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
